import { MapPin, Phone, CalendarClock, User, PenLine } from "lucide-react";
import { AuthImage } from "@/components/AuthImage";
import { MEASURES } from "@/lib/constants";
import { fmtDate } from "@/lib/api";
import { mapsLink } from "@/lib/map";

const show = (v) => (Array.isArray(v) ? (v.length ? v.join("، ") : "-") : v || "-");

const Section = ({ title, children, testId }) => (
  <section className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6" data-testid={testId}>
    <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2"><span className="h-4 w-1 rounded bg-brand-red" />{title}</h3>
    {children}
  </section>
);

const Row = ({ label, value, testId }) => (
  <div className="flex justify-between gap-4 py-2.5 border-b border-slate-100 last:border-0 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-semibold text-end" data-testid={testId}>{show(value)}</span>
  </div>
);

export const ReportView = ({ insp }) => {
  const c = insp.customer || {};
  const ch = insp.checklist || {};
  const m = insp.measurements || {};
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-5">
        <Section title="المقاسات ومواصفات الباب" testId="report-measurements">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
            {MEASURES.map((f) => (
              <div key={f.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs text-slate-500">{f.label} <span className="font-mono">({f.code})</span></p>
                <p className="font-mono text-xl font-bold mt-1" dir="ltr" data-testid={`report-measure-${f.id}`}>{m[f.id] || "—"}<span className="text-xs text-slate-400 ms-1">mm</span></p>
              </div>
            ))}
          </div>
          <Row label="نوع الباب" value={m.door_type} testId="report-door-type" />
          <Row label="الجهاز (الماتور)" value={m.device ? `${m.device} × ${m.device_qty || 1}` : ""} testId="report-device" />
          <Row label="الإضافات" value={m.extras} testId="report-extras" />
          <Row label="ملاحظات الإضافات" value={m.extras_notes} />
          <Row label="اتجاه الفتح" value={m.opening_direction} testId="report-direction" />
          <Row label="عدد الأبواب" value={m.quantity} />
          <Row label="ملاحظات المقاسات" value={m.notes} />
        </Section>
        <Section title="جاهزية الموقع والتمديدات" testId="report-checklist">
          <Row label="جاهزية الموقع" value={ch.site_ready} testId="report-site-ready" />
          <Row label="ملاحظات الجاهزية" value={ch.site_notes} />
          <Row label="تمديدات الأسلاك" value={ch.wiring} />
          <Row label="مسار الأسلاك" value={ch.wiring_route} />
          <Row label="الجهد الكهربائي" value={ch.voltage} />
          <Row label="نوع الجدار" value={ch.wall_type} />
          <Row label="ملاحظات الفتحات" value={ch.openings_notes} />
        </Section>
        <Section title={`الصور والتوثيق (${insp.photos?.length || 0})`} testId="report-photos">
          {!insp.photos?.length && <p className="text-sm text-slate-500">لا توجد صور</p>}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {insp.photos?.map((p) => (
              <figure key={p.id} className="rounded-lg overflow-hidden border border-slate-200">
                <AuthImage path={p.path} className="aspect-[4/3] w-full object-cover" testId={`report-photo-${p.id}`} />
                <figcaption className="text-xs p-2 flex items-center justify-between gap-1">
                  <span className="font-semibold">{p.category}</span>
                  {p.annotated && <PenLine size={13} className="text-brand-navy" />}
                </figcaption>
              </figure>
            ))}
          </div>
        </Section>
      </div>
      <div className="space-y-5">
        {insp.result?.outcome && (
          <Section title="نتيجة المعاينة" testId="report-result">
            <p className="font-bold text-brand-navy" data-testid="report-result-outcome">{insp.result.outcome}</p>
            {insp.result.notes && <p className="text-sm mt-2 bg-slate-50 border border-slate-200 rounded-lg p-3 whitespace-pre-line" data-testid="report-result-notes">{insp.result.notes}</p>}
          </Section>
        )}
        <Section title="العميل والزيارة" testId="report-customer">
          <p className="font-heading text-xl font-bold mb-3" data-testid="report-customer-name">{c.name}</p>
          <div className="space-y-2.5 text-sm">
            <p className="flex items-center gap-2"><Phone size={15} className="text-slate-400" /><a href={`tel:${c.phone}`} dir="ltr" className="font-mono">{c.phone}</a></p>
            <p className="flex items-center gap-2"><MapPin size={15} className="text-slate-400" />{[c.branch, c.area, c.address].filter(Boolean).join(" - ") || "-"}</p>
            {mapsLink(c) && <a href={mapsLink(c)} target="_blank" rel="noreferrer" className="text-blue-700 underline text-sm" data-testid="report-maps-link">فتح الموقع على الخريطة</a>}
            <p className="flex items-center gap-2"><CalendarClock size={15} className="text-slate-400" />{fmtDate(insp.scheduled_at)}</p>
            <p className="flex items-center gap-2"><User size={15} className="text-slate-400" />{insp.agent_name}</p>
          </div>
          {insp.admin_notes && <p className="mt-4 text-sm bg-slate-50 rounded-lg p-3 border border-slate-200">{insp.admin_notes}</p>}
        </Section>
        {insp.status === "completed" && (
          <Section title="تقييم العميل" testId="report-rating">
            {insp.rating ? (
              <>
                <Row label="المندوب" value={`${"★".repeat(insp.rating.agent)}${"☆".repeat(5 - insp.rating.agent)} (${insp.rating.agent}/5)`} testId="report-rating-agent" />
                <Row label="الزيارة" value={`${"★".repeat(insp.rating.visit)}${"☆".repeat(5 - insp.rating.visit)} (${insp.rating.visit}/5)`} testId="report-rating-visit" />
                {insp.rating.comment && <p className="text-sm mt-2 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="report-rating-comment">{insp.rating.comment}</p>}
              </>
            ) : (
              <p className="text-sm text-slate-500" data-testid="report-rating-pending">{insp.rating_link_sent_at ? `أُرسل الرابط ${fmtDate(insp.rating_link_sent_at)}، بانتظار التقييم` : "لم يُرسل رابط التقييم بعد"}</p>
            )}
          </Section>
        )}
        {insp.hold_reason && (
          <Section title="سبب التعليق" testId="report-hold-reason"><p className="text-sm text-amber-800">{insp.hold_reason}</p></Section>
        )}
        <Section title="توقيع العميل" testId="report-signature">
          {insp.signature ? (
            <>
              <AuthImage path={insp.signature.path} className="w-full h-32 object-contain bg-slate-50 rounded-lg border" testId="report-signature-image" />
              <Row label="الموقّع" value={insp.signature.signer_name} />
              <Row label="التاريخ" value={fmtDate(insp.signature.signed_at)} />
            </>
          ) : <p className="text-sm text-slate-500">لم يتم التوقيع بعد</p>}
        </Section>
      </div>
    </div>
  );
};
